import type { BuyTreeNode, BuyTreeResponse, PriceListGroup, PurchasePriceList } from '@/types/buy';

// Разворачиваем дерево закупок в плоский список групп (базис + товар). Базис — это группировка моих офферов по
// адресу (не справочник), поэтому просто обходим узлы. Год узла — только от сервера выпуска А, для запросов; узел без
// года (выпуск Б, у закупки года нет) — обычная группа.
export function flattenBuyTree(tree: BuyTreeResponse | undefined): PriceListGroup[] {
  const groups: PriceListGroup[] = [];
  if (!tree?.nodes) {
    return groups;
  }

  const walk = (nodes: BuyTreeNode[], basis?: BuyTreeNode): void => {
    for (const node of nodes) {
      if (node.type === 'BASIS_ITEM') {
        walk(node.childs ?? [], node);
      } else if (node.type === 'GOODS_ITEM' && basis && node.name) {
        groups.push({
          address: basis.address ?? '',
          addressTitle: basis.addressTitle ?? basis.name ?? '',
          basisId: basis.basisId,
          goods: node.name,
          ...(node.year != null ? { year: node.year } : {}),
          updatedDate: node.updatedDate,
        });
      } else if (node.childs?.length) {
        walk(node.childs, basis);
      }
    }
  };

  walk(tree.nodes);
  return groups;
}

/** Базис с его прайс-листами — для списка «карточка-базис → строки-товары». */
export interface BasisGroup {
  readonly address: string;
  readonly addressTitle: string;
  readonly items: PriceListGroup[];
}

// Группировка плоского списка по базису (адрес + название). Порядок следования сохраняем
// как в дереве (бэкенд уже сортирует осмысленно).
export function groupByBasis(groups: readonly PriceListGroup[]): BasisGroup[] {
  const byKey = new Map<string, BasisGroup>();
  for (const group of groups) {
    const key = `${group.address}|${group.addressTitle}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.items.push(group);
    } else {
      byKey.set(key, { address: group.address, addressTitle: group.addressTitle, items: [group] });
    }
  }
  return [...byKey.values()];
}

/** Диапазон моих цен прайс-листа (по всем строкам и типам предоплаты); null — цен нет. */
export function priceRangeOf(list: PurchasePriceList): { min: number; max: number } | null {
  const prices = list.goodsList.flatMap((item) =>
    item.qualityRows.flatMap((row) =>
      Object.values(row.pricesByPrepaymentMap ?? {}).filter((price): price is number => price != null),
    ),
  );
  if (prices.length === 0) {
    return null;
  }
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

const sameBasis = (node: BuyTreeNode, basis: { readonly address: string; readonly addressTitle: string }): boolean =>
  node.type === 'BASIS_ITEM' &&
  (node.address ?? '') === basis.address &&
  (node.addressTitle ?? node.name ?? '') === basis.addressTitle;

/** Дерево без узла удалённого базиса: удалённое исчезает из списка сразу, не дожидаясь перезагрузки. */
export function withoutBasis(
  tree: BuyTreeResponse | undefined,
  basis: { readonly address: string; readonly addressTitle: string },
): BuyTreeResponse | undefined {
  if (!tree) {
    return tree;
  }
  const prune = (nodes: BuyTreeNode[]): BuyTreeNode[] =>
    nodes.filter((node) => !sameBasis(node, basis)).map((node) => (node.childs ? { ...node, childs: prune(node.childs) } : node));
  return { ...tree, nodes: prune(tree.nodes) };
}

/** Дерево без удалённого прайс-листа (культура на базисе); базис без прайс-листов тоже убирается. */
export function withoutPriceList(
  tree: BuyTreeResponse | undefined,
  list: { readonly address: string; readonly addressTitle: string; readonly goods: string; readonly year?: number },
): BuyTreeResponse | undefined {
  if (!tree) {
    return tree;
  }
  const hasGoods = (nodes: BuyTreeNode[] | undefined): boolean =>
    (nodes ?? []).some((node) => node.type === 'GOODS_ITEM' || hasGoods(node.childs));
  const dropGoods = (nodes: BuyTreeNode[]): BuyTreeNode[] =>
    nodes
      // Год сравнивается, только если он есть и у удаляемой группы, и у узла (сервер выпуска А делит культуру по годам).
      .filter(
        (node) =>
          !(node.type === 'GOODS_ITEM' && node.name === list.goods && (list.year == null || node.year == null || node.year === list.year)),
      )
      .map((node) => (node.childs ? { ...node, childs: dropGoods(node.childs) } : node));
  const walk = (nodes: BuyTreeNode[]): BuyTreeNode[] =>
    nodes.flatMap((node) => {
      if (sameBasis(node, list)) {
        const childs = dropGoods(node.childs ?? []);
        return hasGoods(childs) ? [{ ...node, childs }] : [];
      }
      return [node.childs ? { ...node, childs: walk(node.childs) } : node];
    });
  return { ...tree, nodes: walk(tree.nodes) };
}

/**
 * Год как различитель: только если сервер выпуска А разделил одну культуру базиса на прайс-листы разных лет. Года у
 * закупки нет (решение 2026-11-04), и после выпуска Б сервер отдаёт по одному прайс-листу на культуру — тогда год не
 * показывается нигде; без различителя две строки «Пшеница» на базисе не отличить.
 */
export function yearHint(group: PriceListGroup, basisItems: readonly PriceListGroup[]): number | undefined {
  if (group.year == null) {
    return undefined;
  }
  return basisItems.filter((item) => item.goods === group.goods).length > 1 ? group.year : undefined;
}
